import React, { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useLoadingProgress from "../useLoadingProgress";
import CustomCursor from "./CustomCursor";

export default function Loading({
  onEnter,
  redirectTo = "/home",
  isBlocked = false,
  blockTitle = "Large Screen Required",
  blockMessage = "This portfolio is optimized for large desktop displays and is blocked on mobile/tablet devices.",
  blockHint = "Recommended resolution: 1920 x 1080 or higher.",
  onStillContinue,
}) {
  const { fonts, assets, tools, missions } = useLoadingProgress();
  const navigate = useNavigate();

  const ready = fonts && assets && tools && missions;

  const requiresClick = redirectTo === "/home";

  /* Hide system cursor only when click is required */
  useEffect(() => {
    if (isBlocked || !requiresClick) return;

    document.body.style.cursor = "none";
    return () => {
      document.body.style.cursor = "default";
    };
  }, [isBlocked, requiresClick]);

  /* Auto-enter for non-home routes */
  useEffect(() => {
    if (isBlocked || !ready || requiresClick) return;

    onEnter?.();
    navigate(redirectTo, { replace: true });
  }, [isBlocked, ready, requiresClick, navigate, redirectTo, onEnter]);

  /* Manual continue (home only) */
  const handleContinue = useCallback(() => {
    if (isBlocked || !ready || !requiresClick) return;

    onEnter?.();
    navigate(redirectTo, { replace: true });
  }, [isBlocked, ready, requiresClick, navigate, redirectTo, onEnter]);

  if (isBlocked) {
    const handleStillContinue = () => {
      // window.alert(
      //   "Your device resolution is below the preferred resolution. The app may misbehave, and prolonged use might increase device heat."
      // );
      navigate('/portfolio', { replace: true });
      onStillContinue?.();
    };

    return (
      <div className="w-screen h-screen loadBg flex justify-end items-end">
        <div className="bg-black/50 h-full backdrop-blur-[6px] flex flex-col items-center justify-center w-full px-6 text-center">
          <h1 className="font-sakana text-4xl md:text-5xl text-custom-yellow tracking-wide">
            {blockTitle}
          </h1>
          <p className="mt-5 max-w-xl font-mono text-sm leading-relaxed text-custom-yellow/80">
            {blockMessage}
          </p>
          <p className="mt-3 max-w-xl font-mono text-sm leading-relaxed text-custom-yellow/90">
            {blockHint}
          </p>
          <button
            type="button"
            onClick={handleStillContinue}
            className="mt-8 rounded-lg border border-custom-yellow px-5 py-2 font-sakana text-lg text-custom-yellow transition-all duration-300 hover:bg-custom-yellow hover:text-black"
          >
            {/* Still Continue */}
            CONTINUE
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Custom Cursor ONLY for home entry */}
      {requiresClick && (
        <CustomCursor ready={ready} onClick={handleContinue} />
      )}

      {/* Fullscreen Loading */}
      <div
        className="w-screen h-screen loadBg flex justify-end items-end"
        onClick={handleContinue}
      >
        <div className="bg-black/40 h-full backdrop-blur-[5px] flex flex-col items-center w-full gap-6">
          <h1 className="font-sakana text-7xl text-white tracking-widest mt-[45vh]">
            DIVYA
          </h1>

          <div className="text-white/80 font-mono text-sm space-y-2 mt-6">
            <Status text="Fonts loaded" done={fonts} />
            <Status text="Assets loaded" done={assets} />
            <Status text="Loading tools" done={tools} />
            <Status text="Initializing missions" done={missions} />
          </div>
        </div>
      </div>
    </>
  );
}

function Status({ text, done }) {
  return (
    <div
      className={`transition-all duration-500 ${done
        ? "opacity-100 translate-x-0"
        : "opacity-30 translate-x-2"
        }`}
    >
      {done ? "✓" : "•"} {text}
    </div>
  );
}
